import { useEffect, useRef } from "react";
import { sessionStatsService } from "../services/SessionStatsService";
import type { WebRTCConnection } from "../models";
import { eonStatsCollectorService } from "../services/EonStatsCollector";
import type { StatsMeta } from "../types";

interface UseGameSessionStatsProps {
  rtcConnection: WebRTCConnection | null;
  enabled?: boolean;
  streamConnected?: boolean;
  statsMeta?: StatsMeta;
}

export const useSessionStats = ({
  rtcConnection,
  enabled = true,
  streamConnected = false,
  statsMeta,
}: UseGameSessionStatsProps) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRunningRef = useRef(false);
  const mountedRef = useRef(true);
  const shouldCollectRef = useRef(false);

  /**
   * Collect stats and send to server with retry logic
   */
  const collectAndSend = async (): Promise<void> => {
    if (isRunningRef.current || !shouldCollectRef.current) return;

    isRunningRef.current = true;
    const startTime = Date.now();

    try {
      const stats = await sessionStatsService.getGameSessionStats();

      if (!shouldCollectRef.current) return;
      await eonStatsCollectorService.collectAndSendMetrics(stats);
    } catch (error) {
      console.error("Error in collectAndSend:", error);
    } finally {
      isRunningRef.current = false;

      if (mountedRef.current && shouldCollectRef.current) {
        const elapsed = Date.now() - startTime;
        const nextDelay = Math.max(0, (statsMeta?.interval || 5000) - elapsed);

        timeoutRef.current = setTimeout(collectAndSend, nextDelay);
      }
    }
  };

  /**
   * Start stats collection
   */
  const start = () => {
    if (
      !enabled ||
      !sessionStatsService.initialized ||
      !eonStatsCollectorService.initialized
    )
      return;

    shouldCollectRef.current = true;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    collectAndSend();
  };

  /**
   * Stop stats collection
   */
  const stop = () => {
    shouldCollectRef.current = false;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  /**
   * Initialize service on mount
   */
  useEffect(() => {
    if (!statsMeta) return;
    mountedRef.current = true;

    const { eonServerEndpoint } = statsMeta;

    const sanitizedEndpoint = String(eonServerEndpoint)
      .trim()
      .replace(/^['"`]|['"`]$/g, "")
      .replace(/\/+$/g, "");

    if (rtcConnection) {
      eonStatsCollectorService.initialize(
        {
          eonServerEndpoint: sanitizedEndpoint,
          statsThrottle: 0,
        },
        statsMeta,
      );
      sessionStatsService.initialize({
        rtcConnection,
      });

      eonStatsCollectorService.processGameLaunched();
    }

    return () => {
      mountedRef.current = false;
      stop();
      eonStatsCollectorService.processGameExited();
      sessionStatsService.cleanup();
      eonStatsCollectorService.cleanup();
    };
  }, [rtcConnection, statsMeta]);

  /**
   * Start/stop based on enabled flag and initialization status
   */
  useEffect(() => {
    if (enabled && streamConnected) {
      start();
    } else {
      stop();
    }
  }, [enabled, streamConnected]);

  return {
    start,
    stop,
    isRunning: isRunningRef.current,
  };
};
